/**
 * The two modal screens: the one before the climb and the one that interrupts it.
 *
 * Both are plain DOM, both resolve a promise, and both clean up after themselves
 * — the caller awaits an answer and gets an answer, with no listener left behind
 * and no element left in the tree.
 *
 * The start screen is also where the pointer lock has to be requested from: the
 * CLIMB click is a user gesture, and it is the only one the browser will accept.
 * That is the caller's job to do in the `then`; this module just gets out of the
 * way at the right moment.
 */

import './ui.css';
import { MAX_NAME_LEN, PALETTE } from '@shared/constants';
import {
  COLOR_AUTO,
  SENSITIVITY_MAX,
  SENSITIVITY_MIN,
  sanitizeName,
  settings,
} from '../core/settings';
import { isTouchDevice } from '../core/touch';

const FADE_MS = 300;

/** How long a "really?" stays armed before it forgets you asked. */
const CONFIRM_TIMEOUT_MS = 4000;

export interface StartOptions {
  /** Prefills the name field — normally `settings.name`. */
  defaultName: string;
  /** Preselected swatch, or `COLOR_AUTO` (-1) to let the server choose. */
  defaultColor: number;
  /** Polled while the screen is up, to show whether the relay answered. */
  connected: () => boolean;
}

export interface StartResult {
  name: string;
  color: number;
}

/** What the player chose in the pause menu. */
export type PauseAction = 'resume' | 'restart';

export interface PauseOptions {
  /** Called whenever the sensitivity slider moves. The value is already persisted. */
  onSensitivity?: (value: number) => void;
  /** Called when the mute toggle flips. The value is already persisted. */
  onMute?: (muted: boolean) => void;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function mount(): HTMLElement {
  return document.getElementById('ui-root') ?? document.body;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  parent?.appendChild(node);
  return node;
}

function hex(color: number): string {
  return `#${(color >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
}

/** Build the overlay shell and fade it in on the next frame. */
function openModal(): { overlay: HTMLElement; panel: HTMLElement } {
  const overlay = el('div', 'sk-modal', mount());
  const panel = el('div', 'sk-panel', overlay);
  requestAnimationFrame(() => overlay.classList.add('is-in'));
  return { overlay, panel };
}

function closeModal(overlay: HTMLElement): void {
  overlay.classList.remove('is-in');
  setTimeout(() => overlay.remove(), FADE_MS);
}

/** Nobody wants to name themselves, and nobody should have to. */
function fallbackName(): string {
  return `bones-${Math.floor(Math.random() * 900 + 100)}`;
}

// ---------------------------------------------------------------------------
// start screen
// ---------------------------------------------------------------------------

let startPending: Promise<StartResult> | null = null;

/**
 * Show the title screen and resolve once the player commits to a climb.
 *
 * The chosen name and colour are persisted here, so the caller only has to deal
 * with the value it was handed.
 */
export function showStartScreen(opts: StartOptions): Promise<StartResult> {
  if (startPending !== null) return startPending;

  startPending = new Promise<StartResult>((resolve) => {
    const { overlay, panel } = openModal();

    el('h1', 'sk-title', panel).textContent = 'SKELESTICK';
    el('p', 'sk-tagline', panel).textContent =
      'the mountain rarely forgives — checkpoints must be earned';

    // --- name ------------------------------------------------------------
    const nameField = el('div', 'sk-field', panel);
    el('label', 'sk-label', nameField).textContent = 'NAME';
    const input = el('input', 'sk-input', nameField);
    input.type = 'text';
    input.maxLength = MAX_NAME_LEN;
    input.spellcheck = false;
    input.autocomplete = 'off';
    input.placeholder = fallbackName();
    input.value = sanitizeName(opts.defaultName);

    // --- colour ----------------------------------------------------------
    const colorField = el('div', 'sk-field', panel);
    el('label', 'sk-label', colorField).textContent = 'COLOUR';
    const swatches = el('div', 'sk-swatches', colorField);

    let color = opts.defaultColor;
    const buttons: HTMLButtonElement[] = [];

    const paint = (): void => {
      for (const b of buttons) {
        b.classList.toggle('is-on', Number(b.dataset['color']) === color);
      }
    };

    const addSwatch = (value: number, className: string, label: string): void => {
      const b = el('button', className, swatches);
      b.type = 'button';
      b.dataset['color'] = String(value);
      b.title = label;
      if (value !== COLOR_AUTO) b.style.background = hex(value);
      else b.textContent = 'AUTO';
      b.addEventListener('click', () => {
        color = value;
        paint();
      });
      buttons.push(b);
    };

    addSwatch(COLOR_AUTO, 'sk-swatch sk-swatch--auto', 'Let the server pick a free hue');
    for (const c of PALETTE) addSwatch(c, 'sk-swatch', hex(c));
    paint();

    // --- controls ---------------------------------------------------------
    // A phone player has none of these keys and needs the screen zones instead;
    // listing both would just be two thirds noise either way.
    const touch = isTouchDevice();
    const controls = el('div', 'sk-controls', panel);
    if (touch) {
      controls.append(
        controlLine('LEFT', 'drag to lean'),
        controlLine('RIGHT', 'drag to look'),
        controlLine('PINCH', 'zoom'),
        controlLine('JUMP', 'hold to charge · release to jump'),
      );
    } else {
      controls.append(
        controlLine('WASD', 'lean'),
        controlLine('SPACE', 'hold to charge · release to jump'),
        controlLine('MOUSE', 'orbit'),
        controlLine('WHEEL', 'zoom'),
        controlLine('SHIFT', 'precision lean'),
        controlLine('ESC', 'pause'),
      );
    }

    // --- commit -----------------------------------------------------------
    const climb = el('button', 'sk-btn sk-btn--primary', panel);
    climb.type = 'button';
    climb.textContent = 'CLIMB';

    const statusLine = el('div', 'sk-status-line', panel);

    const refreshStatus = (): void => {
      statusLine.textContent = opts.connected() ? 'RELAY ONLINE' : 'RELAY UNREACHABLE — SOLO CLIMB';
    };
    refreshStatus();
    const poll = setInterval(refreshStatus, 500);

    const submit = (): void => {
      const name = sanitizeName(input.value) || fallbackName();
      settings.name = name;
      settings.color = color;

      clearInterval(poll);
      window.removeEventListener('keydown', onKey);
      closeModal(overlay);
      startPending = null;
      resolve({ name, color });
    };

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    }

    climb.addEventListener('click', submit);
    window.addEventListener('keydown', onKey);

    // Focus, but do not select: a returning player's name is usually the one
    // they want, and highlighting it invites an accidental overwrite. Skipped on
    // touch, where focusing throws the on-screen keyboard over the title card
    // before the player has decided they want to type at all.
    if (!touch) {
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      });
    }
  });

  return startPending;
}

function controlLine(key: string, what: string): HTMLElement {
  const line = document.createElement('div');
  const kbd = document.createElement('kbd');
  kbd.textContent = key;
  line.append(kbd, ` ${what}`);
  return line;
}

// ---------------------------------------------------------------------------
// pause menu
// ---------------------------------------------------------------------------

let pausePending: Promise<PauseAction> | null = null;

/** True while the pause menu owns the screen, so the caller can ignore its own Esc. */
export function isPauseOpen(): boolean {
  return pausePending !== null;
}

/**
 * Show the pause menu and resolve with what the player picked.
 *
 * Restart is deliberately two-step. This is a game whose entire premise is
 * losing an hour of altitude to one bad bounce; a single mis-click must not be
 * able to do the same thing on purpose.
 */
export function showPauseMenu(opts: PauseOptions = {}): Promise<PauseAction> {
  if (pausePending !== null) return pausePending;

  pausePending = new Promise<PauseAction>((resolve) => {
    const { overlay, panel } = openModal();

    el('h1', 'sk-title', panel).textContent = 'PAUSED';

    const resume = el('button', 'sk-btn sk-btn--primary', panel);
    resume.type = 'button';
    resume.textContent = 'RESUME';

    const restart = el('button', 'sk-btn sk-btn--danger', panel);
    restart.type = 'button';
    restart.textContent = 'RESTART';

    let armed = false;
    let armTimer: ReturnType<typeof setTimeout> | null = null;

    const disarm = (): void => {
      armed = false;
      if (armTimer !== null) {
        clearTimeout(armTimer);
        armTimer = null;
      }
      restart.textContent = 'RESTART';
      restart.classList.remove('sk-btn--armed');
    };

    restart.addEventListener('click', () => {
      if (armed) {
        finish('restart');
        return;
      }
      armed = true;
      restart.textContent = 'RESTART? ALL PROGRESS WILL BE LOST';
      restart.classList.add('sk-btn--armed');
      armTimer = setTimeout(disarm, CONFIRM_TIMEOUT_MS);
    });

    // --- mute -------------------------------------------------------------
    const muteRow = el('div', 'sk-row', panel);
    el('span', undefined, muteRow).textContent = 'AUDIO';
    const mute = el('button', 'sk-btn', muteRow);
    mute.type = 'button';
    mute.style.width = 'auto';
    mute.style.margin = '0';
    mute.style.padding = '0.5em 1.2em';

    const paintMute = (): void => {
      mute.textContent = settings.mute ? 'MUTED' : 'ON';
    };
    paintMute();
    mute.addEventListener('click', () => {
      settings.mute = !settings.mute;
      paintMute();
      opts.onMute?.(settings.mute);
      disarm();
    });

    // --- sensitivity ------------------------------------------------------
    const sensRow = el('div', 'sk-row', panel);
    el('span', undefined, sensRow).textContent = 'SENS';
    const range = el('input', 'sk-range', sensRow);
    range.type = 'range';
    range.min = String(SENSITIVITY_MIN);
    range.max = String(SENSITIVITY_MAX);
    range.step = '0.05';
    range.value = String(settings.sensitivity);
    const sensValue = el('span', 'sk-row__value', sensRow);
    sensValue.textContent = settings.sensitivity.toFixed(2);

    range.addEventListener('input', () => {
      const v = Number(range.value);
      if (!Number.isFinite(v)) return;
      settings.sensitivity = v;
      // Read back: `settings` clamps, and the UI should show what was stored.
      const stored = settings.sensitivity;
      sensValue.textContent = stored.toFixed(2);
      opts.onSensitivity?.(stored);
    });

    el('div', 'sk-hint', panel).textContent = 'ESC TO RESUME';

    // --- resolution -------------------------------------------------------
    function finish(action: PauseAction): void {
      if (armTimer !== null) clearTimeout(armTimer);
      window.removeEventListener('keydown', onKey, true);
      closeModal(overlay);
      pausePending = null;
      resolve(action);
    }

    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      // Capture and swallow it: the game's own Esc handler must not re-open the
      // menu we are in the middle of closing.
      e.preventDefault();
      e.stopPropagation();
      finish('resume');
    }

    resume.addEventListener('click', () => finish('resume'));
    window.addEventListener('keydown', onKey, true);

    requestAnimationFrame(() => resume.focus());
  });

  return pausePending;
}
